import {useMemo} from "react";
import DashCard from "../../../../../../../../components/dash-card/dash-card.jsx";
import SentryEchart from "../../../../../../../../components/echarts/sentry-echart.jsx";
import useGraphViewportWidth from "../../../../../../../../hooks/use-graph-viewport-width.js";
import {fluidPx} from "../../../../../../../../utils/responsive-graph.js";
import {FaMars, FaVenus} from "react-icons/fa";

export default function RiskLevel({ data }) {
    const vw = useGraphViewportWidth();
    const protectedCitizens = data?.protectedCitizens;

    const sexes = useMemo(() => Array.isArray(protectedCitizens?.sexes)
        ? protectedCitizens.sexes
            .map(item => {
                const type = item.label?.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR");

                return {
                    label: item.label ?? "",
                    value: Number(item.qty) || 0,
                    type,
                };
            })
            .filter(item => ["feminino", "masculino"].includes(item.type))
            .sort((first, second) => ["feminino", "masculino"].indexOf(first.type)
                - ["feminino", "masculino"].indexOf(second.type))
        : [], [protectedCitizens?.sexes]);

    const ageRanges = useMemo(() => Array.isArray(protectedCitizens?.ageRanges)
        ? protectedCitizens.ageRanges
            .filter(item => item.label?.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR") !== "nao informado")
            .map(item => ({
                label: item.label ?? "",
                value: Number(item.qty) || 0,
            }))
        : [], [protectedCitizens?.ageRanges]);

    const riskLevelsOption = useMemo(() => {
        const items = Array.isArray(protectedCitizens?.riskLevels)
            ? protectedCitizens.riskLevels.map(item => ({
                label: item.label ?? "",
                value: Number(item.qty) || 0,
            }))
            : [];
        const labelFs = fluidPx(12, 21, vw);
        const valueFs = fluidPx(14, 20, vw);
        const labelWidth = fluidPx(120, 180, vw);
        const max = Math.max(...items.map(item => item.value), 1);
        const labels = items.map(item => item.label);
        const values = items.map(item => item.value);

        return {
            grid: {
                top: labelFs,
                left: "6%",
                right: "10%",
                bottom: 0,
                outerBounds: {
                    top: labelFs,
                    left: "6%",
                    right: "10%",
                    bottom: 0,
                },
                outerBoundsContain: "axisLabel",
            },
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: "max-width: 320px; white-space: normal;",
                formatter: params => `${params.name}: ${params.value}`,
            },
            xAxis: {
                type: "value",
                max,
                show: false,
            },
            yAxis: {
                type: "category",
                data: labels,
                inverse: true,
                axisLabel: {show: false},
                axisLine: {show: false},
                axisTick: {show: false},
            },
            series: [
                {
                    type: "bar",
                    barWidth: 13,
                    barCategoryGap: "24%",
                    itemStyle: {
                        color: {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                {offset: 0, color: "#003878"},
                                {offset: 0.28, color: "#0080ED"},
                                {offset: 0.52, color: "#1CEAE6"},
                                {offset: 0.72, color: "#3E98D9"},
                                {offset: 1, color: "#003878"},
                            ],
                        },
                        borderRadius: 20,
                    },
                    label: {
                        show: true,
                        position: [0, -4],
                        align: "left",
                        verticalAlign: "bottom",
                        color: "#AAAAAA",
                        fontSize: labelFs,
                        fontWeight: 400,
                        width: labelWidth,
                        overflow: "truncate",
                        ellipsis: "...",
                        formatter: params => labels[params.dataIndex],
                    },
                    showBackground: true,
                    backgroundStyle: {
                        color: "#3C3C4A",
                        borderRadius: 12,
                    },
                    data: values,
                },
                {
                    type: "bar",
                    barWidth: 13,
                    barCategoryGap: "24%",
                    barGap: "-100%",
                    silent: true,
                    tooltip: {show: false},
                    itemStyle: {color: "transparent"},
                    label: {
                        show: true,
                        position: "right",
                        distance: 5,
                        color: "#FFFFFF",
                        fontSize: valueFs,
                        fontWeight: 600,
                        formatter: params => values[params.dataIndex],
                    },
                    data: values.map(() => max),
                },
            ],
        };
    }, [protectedCitizens?.riskLevels, vw]);

    const ageRangesOption = useMemo(() => {
        const values = ageRanges.map(item => item.value);
        const maxValue = Math.max(...values, 1);

        return {
            grid: {
                top: 24,
                left: 8,
                right: 8,
                bottom: 0,
                containLabel: true,
            },
            tooltip: {
                trigger: "axis",
                axisPointer: {type: "shadow"},
                formatter: params => {
                    const coloredBar = params.find(item => item.seriesName === "Quantidade");
                    const item = ageRanges[coloredBar?.dataIndex];

                    return item?.label ? `${item.label}: <b>${item.value}</b>` : "";
                },
            },
            xAxis: {
                type: "category",
                boundaryGap: true,
                data: ageRanges.map(item => item.label),
                axisLabel: {
                    color: "#AAAAAA",
                    fontSize: 12,
                    interval: 0,
                    width: 55,
                    overflow: "truncate",
                    ellipsis: "...",
                },
                axisLine: {
                    show: true,
                    lineStyle: {color: "#555762"},
                },
                axisTick: {show: false},
            },
            yAxis: {
                type: "value",
                min: 0,
                max: maxValue,
                minInterval: 1,
                axisLabel: {show: false},
                axisTick: {show: false},
                splitLine: {show: false},
                axisLine: {show: false},
            },
            series: [
                {
                    name: "Quantidade",
                    type: "bar",
                    barWidth: "68%",
                    itemStyle: {
                        borderRadius: [4, 4, 0, 0],
                        color: {
                            type: "linear",
                            x: 0,
                            y: 1,
                            x2: 1,
                            y2: 0,
                            colorStops: [
                                {offset: 0, color: "#003878"},
                                {offset: 0.5, color: "#0080ED"},
                                {offset: 1, color: "#1CEAE6"},
                            ],
                        },
                    },
                    label: {
                        show: true,
                        position: "top",
                        distance: 8,
                        color: "#FDFCFF",
                        fontSize: 12,
                        fontWeight: "bold",
                        formatter: params => values[params.dataIndex],
                    },
                    data: values,
                    z: 2,
                },
            ],
        };
    }, [ageRanges]);

    return (
        <article className="col-span-4 box-border -mt-2 grid h-[calc(100%+12px)] min-h-0 min-w-0 w-full grid-rows-[minmax(0,1fr)_clamp(145px,14vh,175px)] gap-y-2">
            <section className="min-h-0 min-w-0 overflow-hidden flex">
                <SentryEchart
                    className="h-full min-h-0 w-full"
                    option={riskLevelsOption}
                    title="Grau de Risco"
                    titleClass="mb-0! translate-y-1"
                />
            </section>
            <section className="grid min-h-0 grid-cols-24 gap-2 -mt-2">
                <div className="col-span-19 flex min-h-0 min-w-0 flex-col overflow-hidden">
                    <h3 className="shrink-0 px-4 text-center text-(length:--font-md)! text-[#AAAAAA]">
                        Idade
                    </h3>
                    <div className="min-h-0 flex-1 px-2 pb-1">
                        <SentryEchart
                            className="h-full min-h-0 w-full"
                            option={ageRangesOption}
                        />
                    </div>
                </div>
                <div className="col-span-5 min-h-0 space-y-2">
                    <h3 className="text-center text-(length:--font-md)! text-[#AAAAAA]">Sexo</h3>
                    {sexes.map(item => {
                        const SexIcon = item.type === "feminino" ? FaVenus : FaMars;

                        return (
                            <DashCard
                                key={item.label}
                                counterCard
                                width="100%"
                                height={52}
                                className="flex mt-3 items-center justify-center gap-2"
                                aria-label={`${item.label}: ${item.value}`}
                            >
                                <SexIcon size={22} aria-hidden="true" />
                                <strong className="text-(length:--font-lg)! 2xl:text-(length:--font-xl)!">
                                    {item.value}
                                </strong>
                            </DashCard>
                        );
                    })}
                </div>
            </section>
        </article>
    );
}
